import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { fetchVCValuation } from '../services/vcValuationService';
import { markValuationAsViewed } from '../services/firestoreService';
import Loader from "../components/Loader";
import Alert from "../components/Alert";
import Footer from "../components/Footer";
import { formatIndianCurrency } from "../utils/currencyUtils";

const POLL_INTERVAL = 15000; // Increased to 15000ms to allow VC method processing time

const ThankYou: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [vcValuation, setVCValuation] = useState<number | null>(null);
  const [methodName, setMethodName] = useState<string>('VC');
  const [error, setError] = useState<string | null>(null);
  const [lastValue, setLastValue] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const pollValuation = async () => {
      try {
        const result = await fetchVCValuation();
        const valueAsString = String(result?.value || '');
        if (result && valueAsString && valueAsString !== "#VALUE!") {
          const newValue = parseFloat(valueAsString);
          
          // Check if value has changed
          if (newValue !== lastValue) {
            setVCValuation(newValue);
            setLastValue(newValue);
            setMethodName(result.method || 'VC');
            
            // Mark valuation as viewed in Firestore
            if (currentUser) {
              await markValuationAsViewed(currentUser.uid, result);
            }
            
            setLoading(false);
            return true;
          }
        }
        return false;
      } catch (err) {
        setError('Failed to fetch valuation. Please try again later.');
        setLoading(false);
        return true;
      }
    };

    const startPolling = async () => {
      const timeout = setTimeout(async () => {
        clearInterval(interval);
        setError("We couldn't fetch your valuation at this time. Please try again.");
        setLoading(false);
      }, 60000); // 60-second timeout to allow for backend processing

      const interval = setInterval(async () => {
        const finished = await pollValuation();
        if (finished) {
          clearTimeout(timeout);
          clearInterval(interval);
        }
      }, POLL_INTERVAL);
    };

    startPolling();
  }, []);

  const handleDownloadPDF = () => {
    window.print();
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };



  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[#9600ff] text-white p-4">
        <Loader />
        <h2 className="text-2xl font-bold mt-4">Processing your valuation...</h2>
        <p>This can take up to 60 seconds while we process your data.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#9600ff] flex flex-col">
      <div className="flex items-center justify-center flex-1 p-4">
      {/* User Info Header */}
      {currentUser && (
        <div className="absolute top-4 right-4 flex items-center space-x-4">
          <div className="flex items-center space-x-2 bg-white/10 rounded-lg px-3 py-2">
            {currentUser.photoURL && (
              <img 
                src={currentUser.photoURL} 
                alt="Profile" 
                className="w-6 h-6 rounded-full"
              />
            )}
            <span className="text-sm font-medium">{currentUser.displayName || currentUser.email}</span>
          </div>
          <button
            onClick={handleLogout}
            className="bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg text-sm transition-all"
          >
            Logout
          </button>
        </div>
      )}
      
      <div className="w-full max-w-2xl mx-auto p-8 bg-white/10 rounded-2xl text-white text-center backdrop-blur-md">
        {error ? (
          <Alert type="error">{error}</Alert>
        ) : (
          <>
            <h1 className="text-4xl font-bold mb-4">Thank You!</h1>
            <p className="opacity-80 mb-6">Your valuation request has been submitted successfully.</p>
            {vcValuation !== null && (
              <div className="bg-white/20 p-6 rounded-xl mb-8">
                <h2 className="text-xl font-semibold opacity-90 mb-2">{methodName} Method Valuation</h2>
                <p className="text-4xl font-bold">
                  {formatIndianCurrency(vcValuation)}
                </p>
                <p className="text-sm opacity-80 mt-4 max-w-md mx-auto">
                  This valuation represents a preliminary assessment based on the information you've provided. For a comprehensive, professional valuation, please reach out to our team for further consultation.
                </p>
              </div>
            )}
          </>
        )}
        <div className="flex justify-center items-center gap-4 flex-wrap">
          <button
            onClick={handleDownloadPDF}
            className="bg-white text-black font-bold py-3 px-6 rounded-lg hover:bg-opacity-90 transition-all"
          >
            Download as PDF
          </button>

          <button
            onClick={() => navigate("/")}
            className="bg-white text-black font-bold py-3 px-6 rounded-lg hover:bg-opacity-90 transition-all"
          >
            Start Over
          </button>
          <button
            onClick={() => navigate("/booking")}
            className="bg-white text-black font-bold py-3 px-6 rounded-lg hover:bg-opacity-90 transition-all"
          >
            Book Consultation
          </button>
          <a
            href="https://wa.link/m2zn1t"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white/10 text-white font-bold py-3 px-6 rounded-lg hover:bg-white/20 transition-all border border-white/30"
          >
            Know More
          </a>
        </div>
      </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ThankYou; 